import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  FileText, Upload, CheckCircle2, Clock, AlertCircle, Loader2,
  Building2, Send, Calendar, RefreshCw, Plus, Eye, Scale, Gavel
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Status-Farben
const STATUS_FARBEN = {
  entwurf: 'bg-slate-100 text-slate-700',
  eingereicht: 'bg-blue-100 text-blue-700',
  bestaetigt: 'bg-emerald-100 text-emerald-700',
  nachforderung: 'bg-amber-100 text-amber-700',
  abgeschlossen: 'bg-green-100 text-green-700',
};

// Typ-Farben
const TYP_FARBEN = {
  betreuungsbericht: 'bg-blue-100 text-blue-700 border-blue-200',
  rechnungslegung: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  vermoegensverzeichnis: 'bg-purple-100 text-purple-700 border-purple-200',
  genehmigungsantrag: 'bg-amber-100 text-amber-700 border-amber-200',
  verguetungsantrag: 'bg-rose-100 text-rose-700 border-rose-200',
};

export default function GerichtPage() {
  // State
  const [config, setConfig] = useState(null);
  const [einreichungen, setEinreichungen] = useState([]);
  const [fristen, setFristen] = useState([]);
  const [typen, setTypen] = useState({});
  const [klienten, setKlienten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('einreichungen');
  
  // Neue Einreichung Dialog
  const [neuOpen, setNeuOpen] = useState(false);
  const [neuForm, setNeuForm] = useState({
    klient_id: '',
    typ: '',
    aktenzeichen: '',
    betreffend_zeitraum: '',
    bemerkungen: ''
  });
  const [creating, setCreating] = useState(false);
  
  // Detail Dialog
  const [selectedEinreichung, setSelectedEinreichung] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, einreichungenRes, fristenRes, typenRes, klientenRes] = await Promise.all([
        fetch(`${API}/api/gericht/konfiguration`),
        fetch(`${API}/api/gericht/einreichungen`),
        fetch(`${API}/api/gericht/fristen`),
        fetch(`${API}/api/gericht/einreichungs-typen`),
        fetch(`${API}/api/betreuung/klienten`)
      ]);
      
      if (configRes.ok) setConfig(await configRes.json());
      if (einreichungenRes.ok) {
        const d = await einreichungenRes.json();
        setEinreichungen(d.einreichungen || []);
      }
      if (fristenRes.ok) {
        const d = await fristenRes.json();
        setFristen(d.fristen || []);
      }
      if (typenRes.ok) {
        const d = await typenRes.json();
        setTypen(d.typen || {});
      }
      if (klientenRes.ok) {
        setKlienten(await klientenRes.json() || []);
      }
    } catch (e) {
      console.error('Laden fehlgeschlagen:', e);
    }
    setLoading(false);
  };

  // Klient auswählen und Gericht laden
  const onKlientSelect = async (klientId) => {
    setNeuForm(prev => ({ ...prev, klient_id: klientId, aktenzeichen: '' }));
    
    try {
      const res = await fetch(`${API}/api/gericht/gericht/${klientId}`);
      if (res.ok) {
        const gericht = await res.json();
        setNeuForm(prev => ({ ...prev, aktenzeichen: gericht.aktenzeichen || '' }));
      }
    } catch (e) {
      console.log('Gericht nicht gefunden');
    }
  };

  // Neue Einreichung erstellen
  const handleCreate = async () => {
    if (!neuForm.klient_id || !neuForm.typ) {
      toast.error('Bitte Klient und Typ auswählen');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/gericht/einreichung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(neuForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Einreichung erstellt');
        setNeuOpen(false);
        setNeuForm({ klient_id: '', typ: '', aktenzeichen: '', betreffend_zeitraum: '', bemerkungen: '' });
        setSelectedEinreichung(data.einreichung);
        setDetailOpen(true);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler beim Erstellen');
      }
    } catch (e) {
      toast.error('Fehler beim Erstellen');
    }
    setCreating(false);
  };

  // Dokument hochladen
  const handleUploadDoc = async (e, typ = 'hauptdokument') => {
    const file = e.target.files?.[0];
    if (!file || !selectedEinreichung) return;
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dokument_typ', typ);
      
      const res = await fetch(`${API}/api/gericht/einreichung/${selectedEinreichung.id}/dokument`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        toast.success('Dokument hochgeladen');
        // Einreichung neu laden
        const einreichungRes = await fetch(`${API}/api/gericht/einreichung/${selectedEinreichung.id}`);
        if (einreichungRes.ok) {
          setSelectedEinreichung(await einreichungRes.json());
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Upload fehlgeschlagen');
    }
    setUploadingDoc(false);
    e.target.value = '';
  };

  // Einreichen
  const handleSubmit = async () => {
    if (!selectedEinreichung) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/gericht/einreichung/${selectedEinreichung.id}/einreichen`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Eingereicht! Eingangsnummer: ${data.eingangs_nummer}`);
        setDetailOpen(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Einreichen fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Einreichen fehlgeschlagen');
    }
    setSubmitting(false);
  };

  // Zustellungsbestätigung
  const handleZustellungsbestaetigung = async () => {
    if (!selectedEinreichung) return;
    try {
      const res = await fetch(`${API}/api/gericht/einreichung/${selectedEinreichung.id}/zustellungsbestaetigung`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Zustellungsbestätigung: ${data.zustellungsbestaetigung_id}`);
        setDetailOpen(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch (e) {
      toast.error('Fehler bei Zustellungsbestätigung');
    }
  };

  // Nachforderung
  const [nachforderungOpen, setNachforderungOpen] = useState(false);
  const [nachforderungForm, setNachforderungForm] = useState({ grund: '', frist: '' });

  const handleNachforderung = async () => {
    if (!selectedEinreichung) return;
    try {
      const formData = new FormData();
      formData.append('grund', nachforderungForm.grund);
      if (nachforderungForm.frist) formData.append('frist', nachforderungForm.frist);

      const res = await fetch(`${API}/api/gericht/einreichung/${selectedEinreichung.id}/nachforderung`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        toast.success('Nachforderung erfasst, Aufgabe erstellt');
        setNachforderungOpen(false);
        setNachforderungForm({ grund: '', frist: '' });
        setDetailOpen(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch (e) {
      toast.error('Fehler bei Nachforderung');
    }
  };

  // Abschließen
  const handleAbschliessen = async () => {
    if (!selectedEinreichung) return;
    try {
      const res = await fetch(`${API}/api/gericht/einreichung/${selectedEinreichung.id}/abschliessen`, { method: 'POST' });
      if (res.ok) {
        toast.success('Einreichung abgeschlossen');
        setDetailOpen(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch (e) {
      toast.error('Fehler beim Abschließen');
    }
  };

  // Frist-Tage berechnen
  const getFristTage = (datum) => {
    if (!datum) return null;
    const heute = new Date();
    const frist = new Date(datum);
    const diff = Math.ceil((frist - heute) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="gericht-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Gavel className="w-6 h-6 text-amber-600" />
            Betreuungsgericht
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Einreichungen an das Betreuungsgericht verwalten
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {config?.modus === 'mock' && (
            <Badge className="bg-amber-100 text-amber-700">Test-Modus</Badge>
          )}
          
          <Dialog open={neuOpen} onOpenChange={setNeuOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="btn-neue-einreichung">
                <Plus className="w-4 h-4" />
                Neue Einreichung
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Neue Einreichung erstellen</DialogTitle>
                <DialogDescription>
                  Erstellen Sie eine neue Einreichung für das Betreuungsgericht
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Klient */}
                <div>
                  <Label>Klient *</Label>
                  <Select value={neuForm.klient_id} onValueChange={onKlientSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Klient auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {klienten.map(k => (
                        <SelectItem key={k.id} value={k.id}>
                          {k.vorname} {k.nachname}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Typ */}
                <div>
                  <Label>Einreichungstyp *</Label>
                  <Select value={neuForm.typ} onValueChange={v => setNeuForm(prev => ({ ...prev, typ: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Typ auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(typen).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          {info.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {neuForm.typ && typen[neuForm.typ] && (
                    <p className="text-xs text-slate-500 mt-1">
                      {typen[neuForm.typ].beschreibung}
                    </p>
                  )}
                </div>
                
                {/* Aktenzeichen */}
                <div>
                  <Label>Aktenzeichen</Label>
                  <Input 
                    value={neuForm.aktenzeichen}
                    onChange={e => setNeuForm(prev => ({ ...prev, aktenzeichen: e.target.value }))}
                    placeholder="z.B. XVII 123/24"
                  />
                </div>
                
                {/* Zeitraum */}
                <div>
                  <Label>Betreffender Zeitraum</Label>
                  <Input 
                    value={neuForm.betreffend_zeitraum}
                    onChange={e => setNeuForm(prev => ({ ...prev, betreffend_zeitraum: e.target.value }))}
                    placeholder="z.B. 01.01.2024 - 31.12.2024"
                  />
                </div>
                
                {/* Bemerkungen */}
                <div>
                  <Label>Bemerkungen</Label>
                  <Textarea 
                    value={neuForm.bemerkungen}
                    onChange={e => setNeuForm(prev => ({ ...prev, bemerkungen: e.target.value }))}
                    placeholder="Optionale Bemerkungen..."
                    rows={2}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setNeuOpen(false)}>Abbrechen</Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Erstellen
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="einreichungen" className="gap-2">
            <FileText className="w-4 h-4" />
            Einreichungen ({einreichungen.length})
          </TabsTrigger>
          <TabsTrigger value="fristen" className="gap-2">
            <Calendar className="w-4 h-4" />
            Fristen ({fristen.length})
          </TabsTrigger>
        </TabsList>

        {/* Einreichungen Tab */}
        <TabsContent value="einreichungen" className="mt-4">
          {einreichungen.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700">Keine Einreichungen</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Erstellen Sie Ihre erste Einreichung für das Betreuungsgericht.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {einreichungen.map(e => (
                <Card 
                  key={e.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => { setSelectedEinreichung(e); setDetailOpen(true); }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Gavel className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">{e.typ_name}</h3>
                          <p className="text-sm text-slate-500">
                            {e.klient_name} • Az. {e.aktenzeichen}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(e.created_date).toLocaleDateString('de-DE')}
                            {e.eingangs_nummer && ` • ${e.eingangs_nummer}`}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge className={TYP_FARBEN[e.typ] || 'bg-slate-100'}>
                          {e.typ}
                        </Badge>
                        <Badge className={STATUS_FARBEN[e.status] || 'bg-slate-100'}>
                          {e.status}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Fristen Tab */}
        <TabsContent value="fristen" className="mt-4">
          {fristen.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700">Keine Fristen</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Keine anstehenden Fristen für Berichte oder Rechnungslegungen.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {fristen.map((f, i) => {
                const tage = getFristTage(f.faellig_am);
                const isUrgent = tage !== null && tage <= 14;
                const isOverdue = tage !== null && tage < 0;
                
                return (
                  <Card key={i} className={isOverdue ? 'border-red-300 bg-red-50' : isUrgent ? 'border-amber-300 bg-amber-50' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isOverdue ? 'bg-red-100' : isUrgent ? 'bg-amber-100' : 'bg-blue-100'
                          }`}>
                            <Clock className={`w-5 h-5 ${
                              isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-800">{f.typ_name}</h3>
                            <p className="text-sm text-slate-500">
                              {f.klient_name} • Az. {f.aktenzeichen}
                            </p>
                            <p className="text-xs text-slate-400">{f.gericht}</p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className={`text-sm font-medium ${
                            isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-slate-700'
                          }`}>
                            {isOverdue ? `${Math.abs(tage)} Tage überfällig` : 
                             tage === 0 ? 'Heute fällig' :
                             `In ${tage} Tagen`}
                          </div>
                          <div className="text-xs text-slate-500">
                            {new Date(f.faellig_am).toLocaleDateString('de-DE')}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedEinreichung && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Gavel className="w-5 h-5 text-amber-600" />
                  {selectedEinreichung.typ_name}
                </DialogTitle>
                <DialogDescription>
                  {selectedEinreichung.klient_name} • Az. {selectedEinreichung.aktenzeichen}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_FARBEN[selectedEinreichung.status]}>
                    {selectedEinreichung.status}
                  </Badge>
                  {selectedEinreichung.eingangs_nummer && (
                    <Badge variant="outline">
                      {selectedEinreichung.eingangs_nummer}
                    </Badge>
                  )}
                  {selectedEinreichung.modus === 'mock' && (
                    <Badge variant="outline" className="text-xs">Mock</Badge>
                  )}
                </div>

                {/* Gericht */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Gericht
                    </h4>
                    <p className="text-sm">{selectedEinreichung.gericht?.name || 'Nicht hinterlegt'}</p>
                    {selectedEinreichung.gericht?.anschrift && (
                      <p className="text-xs text-slate-500 mt-1">{selectedEinreichung.gericht.anschrift}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Dokumente */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-slate-700">Dokumente</h4>
                    {selectedEinreichung.status === 'entwurf' && (
                      <label className="cursor-pointer">
                        <input 
                          type="file" 
                          className="hidden" 
                          accept=".pdf,.docx"
                          onChange={e => handleUploadDoc(e, 'hauptdokument')}
                          disabled={uploadingDoc}
                        />
                        <Button size="sm" variant="outline" asChild>
                          <span>
                            {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                            Hochladen
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                  
                  {(selectedEinreichung.dokumente?.length || 0) === 0 ? (
                    <div className="p-4 bg-slate-50 border border-dashed rounded text-center text-slate-500 text-sm">
                      Noch keine Dokumente hochgeladen
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedEinreichung.dokumente.map(d => (
                        <div key={d.id} className="flex items-center justify-between p-2 bg-emerald-50 border border-emerald-200 rounded">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm">{d.dateiname}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {Math.round(d.groesse / 1024)} KB
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Anlagen */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-slate-700">Anlagen</h4>
                    {selectedEinreichung.status === 'entwurf' && (
                      <label className="cursor-pointer">
                        <input 
                          type="file" 
                          className="hidden" 
                          accept=".pdf,.docx,.jpg,.png"
                          onChange={e => handleUploadDoc(e, 'anlage')}
                          disabled={uploadingDoc}
                        />
                        <Button size="sm" variant="ghost" asChild>
                          <span>
                            <Plus className="w-4 h-4 mr-1" />
                            Anlage
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                  
                  {(selectedEinreichung.anlagen?.length || 0) === 0 ? (
                    <p className="text-sm text-slate-500">Keine Anlagen</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedEinreichung.anlagen.map(a => (
                        <div key={a.id} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm">{a.dateiname}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">Anlage</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setDetailOpen(false)}>
                  Schließen
                </Button>

                {/* Nachforderung-Button (eingereicht oder bestaetigt) */}
                {['eingereicht', 'bestaetigt'].includes(selectedEinreichung.status) && (
                  <Button 
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-1"
                    onClick={() => setNachforderungOpen(true)}
                    data-testid="btn-nachforderung"
                  >
                    <AlertCircle className="w-4 h-4" />
                    Nachforderung
                  </Button>
                )}

                {/* Zustellungsbestätigung (eingereicht) */}
                {selectedEinreichung.status === 'eingereicht' && (
                  <Button 
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
                    onClick={handleZustellungsbestaetigung}
                    data-testid="btn-zustellungsbestaetigung"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Zustellungsbestätigung
                  </Button>
                )}

                {/* Abschließen (bestaetigt oder nachforderung) */}
                {['bestaetigt', 'nachforderung'].includes(selectedEinreichung.status) && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700 gap-1"
                    onClick={handleAbschliessen}
                    data-testid="btn-abschliessen"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Abschließen
                  </Button>
                )}
                
                {selectedEinreichung.status === 'entwurf' && (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={submitting || !selectedEinreichung.dokumente?.length}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Beim Gericht einreichen
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Nachforderung Dialog */}
      <Dialog open={nachforderungOpen} onOpenChange={setNachforderungOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nachforderung erfassen</DialogTitle>
            <DialogDescription>
              Das Gericht verlangt weitere Unterlagen oder Korrekturen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Grund der Nachforderung</Label>
              <Textarea
                value={nachforderungForm.grund}
                onChange={e => setNachforderungForm(prev => ({ ...prev, grund: e.target.value }))}
                placeholder="Was fordert das Gericht an?"
                rows={3}
              />
            </div>
            <div>
              <Label>Frist (optional)</Label>
              <Input
                type="date"
                value={nachforderungForm.frist}
                onChange={e => setNachforderungForm(prev => ({ ...prev, frist: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNachforderungOpen(false)}>Abbrechen</Button>
            <Button onClick={handleNachforderung} className="bg-amber-600 hover:bg-amber-700">
              Nachforderung speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
